import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import EquipmentDroppable from 'af-root/scenes/Company/ScheduleBoard/DailyView/EquipmentDroppable';

import { ScheduleBoardContext } from '@acceligentllc/shared/enums/scheduleBoardContext';
import { ScheduleBoardProperty } from '@acceligentllc/shared/enums/scheduleBoardProperty';

import type { RootState } from 'af-reducers';

import * as ScheduleBoardUtil from 'af-utils/scheduleBoard.util';

import { UNKNOWN_LOCATION_NICKNAME } from 'ab-common/constants/value';

interface OwnProps {
	dueDate: string;
	availabilityLabel: 'available' | 'unavailable' | 'borrowed' | 'loaned';
	groupTitle: Nullable<string>;
	groupId: number;
	hasReason?: boolean;
	isDeleted: boolean;
	isDragAndDropDisabled: boolean;
	hasPermissionsToEditScheduleBoard: boolean;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const EquipmentGroup: React.FC<Props> = (props) => {
	const {
		dueDate,
		isDeleted,
		availabilityLabel,
		groupTitle = UNKNOWN_LOCATION_NICKNAME,
		groupId,
		hasReason = false,
		isDragAndDropDisabled,
		hasPermissionsToEditScheduleBoard,
		disabled,
		equipment,
		equipmentIds = [],
	} = props;

	const hasArrow = React.useMemo(
		() => equipmentIds?.some((_id) => equipment?.[_id]?.isMatched) ?? false
		, [equipment, equipmentIds]);

	const _equipmentIds = React.useMemo(
		() => equipmentIds?.filter((_equId) => equipment?.[_equId]?.showOnScheduleBoard ?? false) ?? []
			.filter((_id, _index, _arr) => _arr.indexOf(_id) === _index)
		, [equipment, equipmentIds]);

	const _showGreenStatusIcon = React.useMemo(
		() => availabilityLabel === 'available' || availabilityLabel === 'borrowed'
		, [availabilityLabel]
	);

	if (isDeleted && equipmentIds.length === 0) {
		return null;
	}

	return (
		<div className="schedule-board-toolbar-equipment-group" key={groupId}>
			{
				hasArrow &&
				<div className="schedule-board-arrow-container">
					<div className={`schedule-board-arrow ${hasArrow ? '--visible' : '--invisible'}`} />
				</div>
			}
			<div className="group-title">
				{
					_showGreenStatusIcon ?
						<img src="/images/elements/ic_state_complete.svg" /> :
						<img src="/images/elements/ic_state_incomplete.svg" />
				}
				<span>{groupTitle}</span>
			</div>
			<hr />
			<EquipmentDroppable
				disabled={disabled || isDeleted}
				draggablePrefix={ScheduleBoardContext.TOOLBAR}
				droppableId={ScheduleBoardUtil.generateDroppableId(ScheduleBoardContext.TOOLBAR, ScheduleBoardProperty.EQUIPMENT, dueDate, `${groupId}`, undefined, availabilityLabel)}
				dueDate={dueDate}
				equipmentIds={_equipmentIds}
				hasPermissionsToEditScheduleBoard={hasPermissionsToEditScheduleBoard}
				hasReason={hasReason}
				isDragAndDropDisabled={isDragAndDropDisabled}
				isToolbar={true}
			/>
		</div>
	);
};

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { dueDate, availabilityLabel, groupId } = ownProps;
	const { equipment, workOrdersByDateDictionary } = state.scheduleBoard;

	const equipmentIds = workOrdersByDateDictionary[dueDate]?.toolbarEquipment[availabilityLabel][groupId];

	const disabled = !state.scheduleBoard.draggedEquipmentId
		|| (state.scheduleBoard.draggedEquipmentId && !equipment?.[state.scheduleBoard.draggedEquipmentId].isOriginalCompany);
	return {
		equipment,
		disabled,
		equipmentIds,
	};
}

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(EquipmentGroup);
